/* eslint-disable react-hooks/exhaustive-deps */
import { createHash } from 'crypto'

import { usePlatform } from '@zippie/did-react-components'
import constate from 'constate'
import React, { useEffect, useState } from 'react'
import secp256k1 from 'secp256k1'

import { useSnackbarContext } from './useSnackBar'
import { createAuthInterceptor } from '../api/accessTokenInterceptor'
import apiClient from '../api/apiClient'

export const useZippieId = () => {
  const { userinfo, isAppSignedIn, platform, isReady, isUserSignedIn } = usePlatform()
  const { openSnackBar } = useSnackbarContext()

  const [pubNubKey, setPubNubKeys] = useState()
  const [accessToken, setAccessToken] = useState('')
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [isLoggedIn, setLoggedIn] = useState(false)
  const [userId, setUserId] = useState('')
  const [nodes, setNodes] = useState([])

  const createSignature = async (type, nonce) => {
    if (!platform) return

    const hashInstruction = createHash('sha256').update(type).update(nonce).digest()
    const sig = await platform.sign('secp256k1', hashInstruction, {
      derive: 'm/0/1',
    })

    return { rs: sig.signature, recovery: sig.recovery, publicKey: sig.publicKey }
  }
  const getNonce = async () => {
    const { data } = await apiClient.get(`/auth/workspace_dashboard/nonce`)
    return data.data
  }

  const signin = async () => {
    if (!platform) return
    const { nonce } = await getNonce()

    try {
      const workspaceInviteCode = window.location.pathname.split('/invite_owner/')[1]
      if (workspaceInviteCode) {
        localStorage.setItem('invite_owner', workspaceInviteCode)
      }
      const signature = await createSignature('signin', nonce)
      const pubKey = Buffer.from(
        secp256k1.publicKeyConvert(Uint8Array.from(Buffer.from(signature.publicKey, 'hex')), false),
      ).toString('hex')
      const inviteCode = window.location.pathname.split('/invite/')[1]

      const { data } = await apiClient.post(`/auth/workspace_dashboard/signin`, {
        pubKey,
        signature,
        inviteCode,
      })
      if (window.location.pathname.includes('invite') || window.location.pathname.includes('welcome')) {
        window.history.pushState({}, '', '/')
      }

      setAccessToken(data.data.token)
      // set default the token
      apiClient.defaults.headers.common['x-access-token'] = data.data.token

      setPubNubKeys({ publishKey: data.data.pubnub.publish, subscribeKey: data.data.pubnub.subscribe })
      const isSuperAdmin = data.data.super
      setNodes(data.data.workspaces)
      setUserId(data.data._id)
      setIsSuperAdmin(isSuperAdmin)
      setLoggedIn(true)

      return data.data.token
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        error.response.data.error.forEach((err) => {
          if (err.path === 'inviteCode' && err.msg.includes('invalid')) {
            openSnackBar({
              type: 'error',
              message: 'The invite has expired or revoked, contact admin',
            })
          }
        })
      }
      await signup()
    }
  }

  const signup = async () => {
    if (accessToken) {
      return
    }
    const { nonce } = await getNonce()

    try {
      const workspaceInviteCode = window.location.pathname.split('/invite_owner/')[1]
      if (workspaceInviteCode) {
        localStorage.setItem('invite_owner', workspaceInviteCode)
      }
      const signature = await createSignature('signup', nonce)
      const pubKey = Buffer.from(
        secp256k1.publicKeyConvert(Uint8Array.from(Buffer.from(signature.publicKey, 'hex')), false),
      ).toString('hex')
      console.info('***', userinfo)
      await apiClient.post(`/auth/workspace_dashboard/signup`, {
        pubKey,
        name: `${userinfo.firstName} ${userinfo.lastName}`,
        email: userinfo.email,
        signature,
      })
      await signin()
    } catch (error) {
      if (error.response) {
        // console.error(error)
      }
    }
  }

  const onStart = async () => {
    if (isAppSignedIn) signin()
  }

  const onUserSignIn = async () => {
    await platform.getDerivedKeyInfo('m/0/1', true)
    await platform.getDerivedKeyInfo('m/0/2', true)
    await platform.getDerivedKeyInfo('m/0/3', true)
  }

  const onUserSignUp = async () => {
    await platform.getDerivedKeyInfo('m/0/1', true)
    await platform.getDerivedKeyInfo('m/0/2', true)
    await platform.getDerivedKeyInfo('m/0/3', true)
  }
  useEffect(() => {
    if (!isAppSignedIn || !isReady || !isUserSignedIn) return
    const init = async () => {
      onStart()
    }

    init()
  }, [platform, isAppSignedIn, isReady, isUserSignedIn])

  useEffect(() => {
    if (isLoggedIn) {
      createAuthInterceptor(apiClient, signin, openSnackBar)
    }
  }, [isLoggedIn])
  return {
    accessToken,
    isSuperAdmin,
    platform,
    isLoggedIn,
    onUserSignIn,
    onUserSignUp,
    nodes,
    userinfo,
    userId,
    pubNubKey,
  }
}

const [ZippieIdProvider, useZippieIdContext] = constate(useZippieId)

export { ZippieIdProvider, useZippieIdContext }
